import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import React from 'react';
import { sortDevices } from '../utils';
import { SortAccessor } from '../types';

type Mode = 'paginated' | 'all';

interface QueryArgs {
  page: number;
  perPage: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
}

interface UseSortableQueryProps<T> {
  queryKey: string;
  scope: readonly unknown[];
  enabled?: boolean;
  defaultOrderBy: string;
  defaultAscOrder?: boolean;
  sortAccessors: Record<string, SortAccessor<T>>;
  queryFn: (args: QueryArgs) => Promise<T[]>;
}

const PAGINATED_PER_PAGE = 20;
const ALL_PER_PAGE = 100;

export function useSortableQuery<T>({
  queryKey,
  scope,
  enabled = true,
  defaultOrderBy,
  defaultAscOrder,
  sortAccessors,
  queryFn,
}: UseSortableQueryProps<T>) {
  const [orderBy, setOrderBy] = React.useState(defaultOrderBy);
  const [ascOrder, setAscOrder] = React.useState(defaultAscOrder ?? true);
  const [mode, setMode] = React.useState<Mode>('paginated');

  const perPage = mode === 'paginated' ? PAGINATED_PER_PAGE : ALL_PER_PAGE;

  const { data, hasNextPage, refetch, fetchNextPage, isRefetching, isFetchingNextPage, isFetching, error } =
    useInfiniteQuery({
      enabled,
      // In paginated mode the server applies the sort, so orderBy/ascOrder are
      // part of the query identity — toggling them must refetch. In all-mode
      // sort is client-side, so we deliberately exclude them.
      queryKey: [queryKey, mode, ...(mode === 'paginated' ? [orderBy, ascOrder] : []), ...scope],
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        queryFn({
          page: pageParam as number,
          perPage,
          ...(mode === 'paginated' && {
            orderBy,
            order: ascOrder ? ('asc' as const) : ('desc' as const),
          }),
        }),
      getNextPageParam: (lastPage: T[], pages) => (lastPage.length === perPage ? pages.length + 1 : undefined),
      placeholderData: keepPreviousData,
    });

  React.useEffect(() => {
    if (mode === 'all' && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [mode, hasNextPage, isFetchingNextPage]);

  const pendingLoadAllRef = React.useRef<{
    resolve: () => void;
    reject: (reason: unknown) => void;
  } | null>(null);

  /**
   * Resolves when all data already loaded.
   * Otherwise, returns pendingLoadAllRef promise so consumer knows it's in flight.
   */
  const loadAll = React.useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (mode === 'all' && !hasNextPage && !isFetching && !isFetchingNextPage) {
        resolve();
        return;
      }
      pendingLoadAllRef.current = { resolve, reject };
      if (mode !== 'all') setMode('all');
    });
  }, [mode, hasNextPage, isFetching, isFetchingNextPage]);

  React.useEffect(() => {
    const pending = pendingLoadAllRef.current;
    if (!pending) return;
    if (error) {
      pending.reject(error);
      pendingLoadAllRef.current = null;
      return;
    }
    if (mode === 'all' && !hasNextPage && !isFetching && !isFetchingNextPage) {
      pending.resolve();
      pendingLoadAllRef.current = null;
    }
  }, [mode, hasNextPage, isFetching, isFetchingNextPage, error]);

  React.useEffect(() => {
    return () => {
      if (pendingLoadAllRef.current) {
        pendingLoadAllRef.current.reject(new Error('unmounted'));
        pendingLoadAllRef.current = null;
      }
    };
  }, []);

  const flattenedData = React.useMemo<T[]>(() => data?.pages.flatMap((p: T[]) => p) ?? [], [data]);

  const sortedData = React.useMemo<T[]>(() => {
    if (mode === 'paginated') return flattenedData;
    const accessor = sortAccessors[orderBy];
    if (!accessor) return flattenedData;
    return sortDevices(flattenedData, accessor, ascOrder);
  }, [mode, flattenedData, orderBy, ascOrder, sortAccessors]);

  const handleSort = React.useCallback(
    (columnId: string) => {
      if (!ascOrder) {
        setOrderBy(defaultOrderBy);
      }
      if (orderBy === columnId) {
        setAscOrder((x) => !x);
      } else {
        setOrderBy(columnId);
      }
      if (mode === 'paginated' && columnId !== defaultOrderBy) {
        setMode('all');
      }
    },
    [ascOrder, orderBy, mode, defaultOrderBy]
  );

  const isRefreshing = isRefetching || isFetching || isFetchingNextPage;

  return {
    sortedData,
    orderBy,
    ascOrder,
    mode,
    handleSort,
    refetch,
    isRefreshing,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    loadAll,
  };
}
